// Business types and product field groups. A business type only picks which
// groups of product fields the app shows; every business has every column.
// Group fields stored in products.attributes are display-only: business rules
// never read them.
import type { JsonValue, TenantSettingsRow } from '@ims/data'

export type GroupId =
  | 'brand'
  | 'pricing'
  | 'packs'
  | 'shelf'
  | 'trade'
  | 'batches'
  | 'variants'
  | 'serials'
  | 'wholesale'
  | 'scale'
  | 'pharmacy'
  | 'apparel'
  | 'electronics'
  | 'photo'
  | 'custom'

export type AttributeType = 'text' | 'number' | 'date' | 'yesno' | 'select'

/** A field kept in products.attributes (group fields and custom fields). */
export interface AttributeField {
  key: string
  label: string
  type: AttributeType
  options?: string[]
  placeholder?: string
}

export interface FieldGroup {
  id: GroupId
  label: string
  description: string
  attributes?: AttributeField[]
  /** False until the feature ships; shown as "coming soon". */
  available: boolean
}

export const FIELD_GROUPS: FieldGroup[] = [
  {
    id: 'brand',
    label: 'Brand & manufacturer',
    description: 'Filter and search products by brand.',
    available: true,
  },
  {
    id: 'pricing',
    label: 'MRP',
    description: 'The printed maximum retail price, with a warning when you sell above it.',
    available: true,
  },
  {
    id: 'packs',
    label: 'Pack sizes & extra barcodes',
    description: 'Scan a box barcode to sell 12 at once; more than one barcode per product.',
    available: true,
  },
  {
    id: 'shelf',
    label: 'Shelf & reorder',
    description: 'Rack/shelf location, tags, and how much to reorder.',
    available: true,
  },
  {
    id: 'trade',
    label: 'HS code',
    description: 'Harmonised System code for customs and tax reports.',
    available: true,
  },
  {
    id: 'batches',
    label: 'Batches & expiry',
    description: 'Batch number, manufacture and expiry dates; sell the earliest expiry first.',
    available: true,
  },
  {
    id: 'variants',
    label: 'Variants',
    description: 'Size, colour and other options, each with its own code, price and stock.',
    available: true,
  },
  {
    id: 'serials',
    label: 'Serial / IMEI numbers',
    description: 'Track each unit by serial or IMEI, with warranty.',
    available: true,
  },
  {
    id: 'wholesale',
    label: 'Wholesale & minimum prices',
    description: 'A wholesale price for wholesale customers, and a price staff can’t go below.',
    available: true,
  },
  {
    id: 'scale',
    label: 'Weighing-scale barcodes',
    description: 'Scan stickers from a label scale: item code plus weight or price.',
    available: true,
  },
  {
    id: 'pharmacy',
    label: 'Medicine details',
    description: 'Generic name, strength, dosage form and prescription.',
    available: true,
    attributes: [
      { key: 'generic_name', label: 'Generic name', type: 'text', placeholder: 'Paracetamol' },
      { key: 'strength', label: 'Strength', type: 'text', placeholder: '500 mg' },
      {
        key: 'dosage_form',
        label: 'Dosage form',
        type: 'select',
        options: [
          'Tablet',
          'Capsule',
          'Syrup',
          'Suspension',
          'Injection',
          'Ointment',
          'Drops',
          'Inhaler',
          'Other',
        ],
      },
      { key: 'prescription_required', label: 'Prescription required', type: 'yesno' },
    ],
  },
  {
    id: 'apparel',
    label: 'Clothing details',
    description: 'Material and season.',
    available: true,
    attributes: [
      { key: 'material', label: 'Material', type: 'text', placeholder: 'Cotton' },
      {
        key: 'season',
        label: 'Season',
        type: 'select',
        options: ['All season', 'Summer', 'Winter', 'Festival'],
      },
    ],
  },
  {
    id: 'electronics',
    label: 'Electronics details',
    description: 'Model number.',
    available: true,
    attributes: [{ key: 'model_no', label: 'Model no.', type: 'text' }],
  },
  {
    id: 'photo',
    label: 'Photos',
    description: 'A picture of the product. Restaurants see it on the menu when taking an order.',
    available: true,
  },
  {
    id: 'custom',
    label: 'Custom fields',
    description: 'Your own product fields, set up below.',
    available: true,
  },
]

export const GROUP_BY_ID = Object.fromEntries(FIELD_GROUPS.map((g) => [g.id, g])) as Record<
  GroupId,
  FieldGroup
>

export type BusinessType = TenantSettingsRow['business_type']

export const BUSINESS_TYPES: {
  id: BusinessType
  label: string
  example: string
  groups: GroupId[]
}[] = [
  {
    id: 'kirana',
    label: 'Kirana & grocery',
    example: 'Rice, noodles, oil, snacks',
    groups: ['brand', 'pricing', 'packs', 'shelf', 'wholesale'],
  },
  {
    id: 'pharmacy',
    label: 'Pharmacy',
    example: 'Medicines, surgical items',
    groups: ['brand', 'pricing', 'packs', 'shelf', 'batches', 'pharmacy'],
  },
  {
    id: 'hardware',
    label: 'Hardware & paints',
    example: 'Tools, pipes, cement, paint',
    groups: ['brand', 'packs', 'shelf', 'trade', 'wholesale'],
  },
  {
    id: 'apparel',
    label: 'Clothing & footwear',
    example: 'Shirts, sarees, shoes',
    groups: ['brand', 'variants', 'apparel'],
  },
  {
    id: 'electronics',
    label: 'Mobile & electronics',
    example: 'Phones, chargers, appliances',
    groups: ['brand', 'pricing', 'serials', 'electronics'],
  },
  {
    id: 'cosmetics',
    label: 'Cosmetics & beauty',
    example: 'Creams, make-up, perfume',
    groups: ['brand', 'pricing', 'batches'],
  },
  {
    id: 'bakery',
    label: 'Bakery & sweets',
    example: 'Cakes, bread, mithai',
    groups: ['packs', 'batches', 'scale'],
  },
  {
    id: 'restaurant',
    label: 'Restaurant & café',
    example: 'Tables, dishes, drinks',
    groups: ['brand', 'photo'],
  },
  {
    id: 'workshop',
    label: 'Workshop & repair',
    example: 'Spare parts, labour, servicing',
    groups: ['brand', 'shelf'],
  },
  {
    id: 'wholesale',
    label: 'Wholesale & distribution',
    example: 'Cartons, bulk goods',
    groups: ['brand', 'pricing', 'packs', 'trade', 'wholesale'],
  },
  {
    id: 'general',
    label: 'Other shop',
    example: 'Stationery, gifts, anything else',
    groups: ['brand', 'shelf'],
  },
]

export function defaultGroups(type: BusinessType | null | undefined): GroupId[] {
  return BUSINESS_TYPES.find((t) => t.id === type)?.groups ?? ['brand', 'shelf']
}

const isGroupId = (v: unknown): v is GroupId => typeof v === 'string' && v in GROUP_BY_ID

/** The groups this business shows: its own choice, else its type's defaults. Unreleased groups are dropped. */
export function enabledGroups(
  settings: Pick<TenantSettingsRow, 'business_type' | 'field_groups'> | null | undefined,
): Set<GroupId> {
  const chosen = Array.isArray(settings?.field_groups)
    ? settings.field_groups.filter(isGroupId)
    : defaultGroups(settings?.business_type)
  return new Set(chosen.filter((g) => GROUP_BY_ID[g].available))
}

/** Custom fields from settings, keeping only well-formed entries. */
export function customFields(
  settings: Pick<TenantSettingsRow, 'custom_fields'> | null | undefined,
): AttributeField[] {
  const raw = settings?.custom_fields
  if (!Array.isArray(raw)) return []
  const out: AttributeField[] = []
  for (const item of raw) {
    if (!item || typeof item !== 'object' || Array.isArray(item)) continue
    const { key, label, type, options } = item as Record<string, JsonValue>
    if (typeof key !== 'string' || typeof label !== 'string') continue
    const t = (['text', 'number', 'date', 'yesno', 'select'] as const).find((x) => x === type)
    if (!t) continue
    out.push({
      key,
      label,
      type: t,
      options: Array.isArray(options)
        ? options.filter((o): o is string => typeof o === 'string')
        : undefined,
    })
  }
  return out
}

/** Attribute fields to show for the enabled groups (group fields, then custom fields). */
export function attributeFields(
  groups: ReadonlySet<GroupId>,
  custom: AttributeField[],
): { group: FieldGroup; fields: AttributeField[] }[] {
  const out: { group: FieldGroup; fields: AttributeField[] }[] = []
  for (const g of FIELD_GROUPS) {
    if (!groups.has(g.id)) continue
    if (g.id === 'custom') {
      if (custom.length) out.push({ group: g, fields: custom })
    } else if (g.attributes?.length) {
      out.push({ group: g, fields: g.attributes })
    }
  }
  return out
}

/** "Rack no." → "rack_no" (a stable key for a new custom field). */
export function fieldKey(label: string, taken: ReadonlySet<string>): string {
  const base =
    label
      .normalize('NFKD')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '')
      .slice(0, 30) || 'field'
  let key = base
  for (let n = 2; taken.has(key); n++) key = `${base}_${n}`
  return key
}

/** Attributes without empty values (so products don't store { strength: "" }). */
export function cleanAttributes(
  values: Record<string, JsonValue | undefined>,
): Record<string, JsonValue> | null {
  const out: Record<string, JsonValue> = {}
  for (const [k, v] of Object.entries(values)) {
    if (v === undefined || v === null || v === '') continue
    out[k] = v
  }
  return Object.keys(out).length ? out : null
}

/** Text shown for an attribute value. */
export function formatAttribute(field: AttributeField, value: JsonValue | undefined): string {
  if (value === undefined || value === null || value === '') return '—'
  if (field.type === 'yesno') return value ? 'Yes' : 'No'
  return String(value)
}

/** Tags as a clean list: "otc, fever , OTC" → ["otc", "fever"]. */
export function parseTags(text: string | null | undefined): string[] {
  const seen = new Set<string>()
  const out: string[] = []
  for (const raw of (text ?? '').split(/[,;]/)) {
    const tag = raw.trim()
    if (!tag || seen.has(tag.toLowerCase())) continue
    seen.add(tag.toLowerCase())
    out.push(tag)
  }
  return out
}
